import { FormBlockInstance } from '../api-types';

export const FILE_UPLOAD_TYPE = 'file_upload';
export const FILE_UPLOAD_TYPE_VERSION = 1;

const MEGABYTE = 1024 * 1024;

/** Mirrors pro-core's default, so the picker is never unbounded if config arrives thin. */
export const DEFAULT_MAX_SIZE_BYTES = 15 * MEGABYTE;

/** The kinds of file pro-core can be told to accept; a block without a list means PDF only. */
export type FileUploadType = 'pdf' | 'jpeg' | 'png';

const TYPE_DETAILS: Record<
  FileUploadType,
  { mimeType: string; extension: RegExp; label: string }
> = {
  pdf: { mimeType: 'application/pdf', extension: /\.pdf$/i, label: 'PDF' },
  jpeg: { mimeType: 'image/jpeg', extension: /\.jpe?g$/i, label: 'JPEG' },
  png: { mimeType: 'image/png', extension: /\.png$/i, label: 'PNG' }
};

const DEFAULT_ALLOWED_TYPES: FileUploadType[] = ['pdf'];

export interface FileUploadConfig {
  maxSizeBytes: number;
  allowedTypes: FileUploadType[];
}

/** Where an upload is in its life, as pro-core reports it to the respondent. */
export type FileUploadStatus =
  | 'PENDING_SCAN'
  | 'CLEAN'
  | 'REJECTED'
  | 'SCAN_FAILED'
  | 'FILE_TOO_LARGE'
  | 'REMOVING';

/**
 * The respondent's view of a `file_upload@1` answer. pro-core strips the scanner's own detail (the
 * verdict, signature names, storage key) before it reaches the browser, so only identity and status
 * are here — and all of it is written by the server, never by this app.
 */
export interface FileUploadAnswer {
  fileRefId: string;
  status: FileUploadStatus;
  originalFilename: string | null;
}

const STATUSES: ReadonlySet<string> = new Set([
  'PENDING_SCAN',
  'CLEAN',
  'REJECTED',
  'SCAN_FAILED',
  'FILE_TOO_LARGE',
  'REMOVING'
]);

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

export const readFileUploadConfig = (
  block: FormBlockInstance
): FileUploadConfig => {
  const interaction = asRecord(asRecord(block.config).interaction);
  const max = interaction.maxSizeBytes;
  // Names this app does not know are dropped, never guessed at; nothing left means the PDF default.
  const listed = Array.isArray(interaction.allowedTypes)
    ? (interaction.allowedTypes as unknown[])
    : [];
  const known = (Object.keys(TYPE_DETAILS) as FileUploadType[]).filter(type =>
    listed.includes(type)
  );
  return {
    allowedTypes: known.length ? known : DEFAULT_ALLOWED_TYPES,
    maxSizeBytes:
      typeof max === 'number' && Number.isFinite(max) && max > 0
        ? Math.floor(max)
        : DEFAULT_MAX_SIZE_BYTES
  };
};

export const readFileUploadAnswer = (
  answer: unknown
): FileUploadAnswer | null => {
  const record = asRecord(answer);
  if (
    typeof record.fileRefId !== 'string' ||
    !record.fileRefId.trim() ||
    typeof record.status !== 'string' ||
    !STATUSES.has(record.status)
  ) {
    return null;
  }
  return {
    fileRefId: record.fileRefId,
    status: record.status as FileUploadStatus,
    originalFilename:
      typeof record.originalFilename === 'string'
        ? record.originalFilename
        : null
  };
};

/**
 * Answered means the scan cleared the file. A file still being scanned, rejected, unscannable (the
 * check failed or the file was too large for it), or being removed is not an answer yet: pro-core
 * refuses a submit over any of them (even on an optional block), so gating Next on the same rule
 * means a required question never lets someone walk into that refusal.
 * Immediate feedback only — the server confirms a clean file against storage and its verdict wins.
 */
export const isFileUploadAnswered = (answer: unknown): boolean =>
  readFileUploadAnswer(answer)?.status === 'CLEAN';

/** Megabytes for the limit line, to one decimal: 15 reads "15", 12.4 MB reads "12.4". */
export const formatMegabytes = (bytes: number): number =>
  Math.round((bytes / MEGABYTE) * 10) / 10;

/**
 * Which allowed type a picked file is, or null. By declared type, or by name only when the browser
 * declared no type at all (it leaves the type empty for some sources). An explicit type that is not
 * allowed is never rescued by the filename: the request carries the resolved type's mime type, so
 * accepting `image/gif` + `photo.png` here would walk around the server's own check instead of
 * failing the selection. The server still judges the bytes themselves.
 */
export const resolveUploadType = (
  file: { name: string; type: string },
  allowed: readonly FileUploadType[]
): FileUploadType | null =>
  allowed.find(type =>
    file.type
      ? file.type === TYPE_DETAILS[type].mimeType
      : TYPE_DETAILS[type].extension.test(file.name)
  ) ?? null;

export const mimeTypeOf = (type: FileUploadType): string =>
  TYPE_DETAILS[type].mimeType;

/** The picker's `accept` list: mime types plus extensions, for sources that declare no type. */
export const acceptOf = (allowed: readonly FileUploadType[]): string =>
  allowed
    .flatMap(type => [
      TYPE_DETAILS[type].mimeType,
      type === 'jpeg' ? '.jpg,.jpeg' : `.${type}`
    ])
    .join(',');

/**
 * The allowed types that are images, in order. A phone only offers its photo library and camera when the
 * picker's `accept` is images alone, so these get an input of their own beside the general one.
 */
export const imageTypesOf = (
  allowed: readonly FileUploadType[]
): FileUploadType[] => allowed.filter(type => type !== 'pdf');

/** `accept` for the photo input: mime types only, since extensions make a phone fall back to its file browser. */
export const photoAcceptOf = (allowed: readonly FileUploadType[]): string =>
  imageTypesOf(allowed)
    .map(type => TYPE_DETAILS[type].mimeType)
    .join(',');

/** "PDF", "PDF or JPEG", "PDF, JPEG, or PNG": the allowed types as the respondent reads them. */
export const describeTypes = (
  allowed: readonly FileUploadType[],
  locale: string
): string =>
  new Intl.ListFormat(locale, { style: 'long', type: 'disjunction' }).format(
    allowed.map(type => TYPE_DETAILS[type].label)
  );
